import { useEffect, useCallback } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useMapStore } from '@/stores/mapStore';
import { useRoutingStore } from '@/stores/routingStore';
import config from '@/config';

export interface KeyboardShortcut {
  key: string;
  modifiers?: ('ctrl' | 'shift' | 'alt' | 'meta')[];
  description: string;
  action: () => void;
  category: 'navigation' | 'panels' | 'map' | 'general';
}

export function useKeyboardShortcuts() {
  const { openPanel, closePanel, togglePanel, panels, addNotification } = useUIStore();
  const { zoomIn, zoomOut, setStreetViewCoordinates, viewport, goToUserLocation, setShowLocationPanel, showLocationPanel } = useMapStore();
  const { reset: resetRouting } = useRoutingStore();

  // Focus search
  const focusSearch = useCallback(() => {
    const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
    if (searchInput) {
      searchInput.focus();
      searchInput.select();
    }
  }, []);

  // Define shortcuts
  const shortcuts: KeyboardShortcut[] = [
    // General
    {
      key: '/',
      description: 'Focus search',
      action: focusSearch,
      category: 'general',
    },
    {
      key: 'Escape',
      description: 'Close current panel / Cancel',
      action: () => {
        // Close panels in priority order
        if (panels.directionsPanel) {
          resetRouting();
          closePanel('directionsPanel');
        } else if (panels.placeDetailsPanel) {
          closePanel('placeDetailsPanel');
        } else if (panels.measurePanel) {
          closePanel('measurePanel');
        } else if (panels.savedPlacesPanel) {
          closePanel('savedPlacesPanel');
        } else if (panels.offlinePanel) {
          closePanel('offlinePanel');
        } else if (panels.nearbyPanel) {
          closePanel('nearbyPanel');
        }
      },
      category: 'general',
    },
    {
      key: '?',
      modifiers: ['shift'],
      description: 'Show keyboard shortcuts',
      action: () => {
        addNotification({
          type: 'info',
          message: 'Press ? for shortcuts • / for search • D for directions • Esc to close panels',
          duration: 5000,
        });
      },
      category: 'general',
    },

    // Panels
    {
      key: 'd',
      description: 'Open directions',
      action: () => {
        openPanel('directionsPanel');
      },
      category: 'panels',
    },
    {
      key: 's',
      description: 'Toggle saved places',
      action: () => togglePanel('savedPlacesPanel'),
      category: 'panels',
    },
    {
      key: 'n',
      description: 'Toggle nearby places',
      action: () => togglePanel('nearbyPanel'),
      category: 'panels',
    },
    {
      key: 'm',
      description: 'Toggle measure tool',
      action: () => togglePanel('measurePanel'),
      category: 'panels',
    },
    // Offline maps is feature-flagged off by default (partial/unverified pipeline).
    ...(config.features.offline
      ? [{
          key: 'o',
          description: 'Toggle offline maps',
          action: () => togglePanel('offlinePanel'),
          category: 'panels',
        } as KeyboardShortcut]
      : []),
    {
      key: 'l',
      description: 'Toggle layers panel',
      action: () => togglePanel('layersPanel'),
      category: 'panels',
    },
    // Street View viewer is a procedural placeholder, not real imagery; flag-gated off.
    ...(config.features.streetView
      ? [{
          key: 'v',
          description: 'Open street view',
          action: () => setStreetViewCoordinates(viewport.center),
          category: 'panels',
        } as KeyboardShortcut]
      : []),

    // Map controls
    {
      key: 'g',
      description: 'Go to my location',
      action: () => goToUserLocation(),
      category: 'map',
    },
    {
      key: 'G',
      modifiers: ['shift'],
      description: 'Toggle location panel',
      action: () => setShowLocationPanel(!showLocationPanel),
      category: 'panels',
    },
    {
      key: '+',
      description: 'Zoom in',
      action: zoomIn,
      category: 'map',
    },
    {
      key: '=',
      description: 'Zoom in',
      action: zoomIn,
      category: 'map',
    },
    {
      key: '-',
      description: 'Zoom out',
      action: zoomOut,
      category: 'map',
    },
  ];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        // Only allow Escape in inputs
        if (event.key !== 'Escape') {
          return;
        }
      }

      // Find matching shortcut
      for (const shortcut of shortcuts) {
        const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase() ||
          event.key === shortcut.key;

        if (!keyMatches) continue;

        // Check modifiers
        const modifiers = shortcut.modifiers || [];
        const ctrlRequired = modifiers.includes('ctrl');
        const shiftRequired = modifiers.includes('shift');
        const altRequired = modifiers.includes('alt');
        const metaRequired = modifiers.includes('meta');

        if (
          ctrlRequired !== (event.ctrlKey || event.metaKey) ||
          shiftRequired !== event.shiftKey ||
          altRequired !== event.altKey ||
          metaRequired !== event.metaKey
        ) {
          continue;
        }

        // Execute shortcut
        event.preventDefault();
        shortcut.action();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts, panels]);

  return { shortcuts };
}
